/**
 * Visible sentences for each public route. Each one is 10–25 words.
 */
const { PAGE_PATHS } = require('./canonicals');

/** @param {string} sentence */
function wordCount(sentence) {
  return sentence
    .replace(/[—–]/g, ' ')
    .replace(/[^A-Za-z0-9$/'’.+-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
}

const EARLY_ADOPTER =
  'Early adopters pay $10 per month, locked for the lifetime of the account, and that price never goes up.';

const HOME = {
  lede: 'Quiet, focused AI chat — private by default — is how this Hesychia product is built.',
  memory:
    'It remembers what you told it and your documents, so you never start over, and conversation memory is a setting you control.',
  stored: 'We store your account, your conversations, and the documents you upload.',
  notSold: 'We do not sell your data, and we do not use advertising cookies.',
  cancel:
    'Canceling does not delete documents, and they stay until you delete them or delete the account, and account deletion is a soft-delete.',
  memoryFeature: 'It remembers what you told it and your documents, so you never start over.',
  documentsFeature:
    'Your documents stay stored and searchable, and answers are grounded in your own files.',
  businessFeature:
    'Business accounts are simple management for your team, and company managers can manage team accounts.',
  earlyAdopter: EARLY_ADOPTER,
  roadmapImage:
    'Image generation is on the roadmap, so a prompt can create a picture inside Hesychia.',
  roadmapVision:
    'Upload an image with your prompt and Hesychia reads the picture, so you can ask about what is in it.',
};

const PRICING = {
  price: '$10/month, flat, with no ads, no upsells, and one price.',
  lock: EARLY_ADOPTER,
  plan: 'One public plan covers quiet, focused AI chat, your documents, and business accounts for your team.',
  compare: 'Hard to justify $20 a month, and this is $10.',
};

const PRIVACY = {
  stored:
    'Your account (email, name, and an optional company), your conversations, and the documents you upload so answers can use your files.',
  analytics:
    'Optional product analytics, if you accept them, record actions such as sign-in and checkout, and they do not include message or document contents.',
  notSold: 'We do not sell your information, and we do not use advertising cookies.',
  thirdParties:
    'Information you enter is not shared with third parties without your consent, except to operate the service or when the law requires it.',
  binding: 'The Terms of Service and the analytics section are the binding description.',
  cancel: 'Canceling stops paid access at the end of the current period.',
  documents:
    'It does not delete uploaded documents, and documents stay in your workspace until you delete them or until you delete the account.',
  softDelete:
    'Account deletion is a soft-delete of profile details, conversations, uploaded documents, and authentication records.',
  purge:
    'Those records may remain for administration, security, and audit, and there is no automatic hard purge.',
};

const CHANGELOG = {
  memory:
    'Account settings can use earlier turns in a chat as context, so you do not re-explain the same project on every message.',
  memoryOff: 'It stays off until you turn it on in account settings.',
  deletion: 'A public page explains how to delete an account and that deletion is a soft-delete.',
  documents:
    'Upload documents and search them from chat, and personal and company libraries stay separate.',
  subscriptions:
    'Upgrade, change, or cancel from the account page, and the public price is $10 a month.',
};

const ACCOUNT_DELETION = {
  intro:
    'Use the steps below to request deletion of your Hesychia account, and this page does not require sign-in to read.',
  open: 'Open Hesychia in the mobile app or at hesychia.ai for your account.',
  signIn: 'Sign in with the account you want deleted before you continue.',
  goToAccount: 'Go to the Account page after you sign in to Hesychia.',
  confirm: 'Choose Delete my account, then confirm by typing your account email.',
  deactivated:
    'Your account is marked deleted and set inactive, and your conversations are hidden from the product.',
  loseAccess: 'You lose access to Hesychia and cannot sign in again with that account.',
  sessions: 'Active sessions are invalidated, and the refresh tokens are blacklisted.',
  softDelete:
    'Hesychia currently uses a soft-delete process, and this is not a full erasure of all stored data.',
  remain: 'After you delete your account, personal data associated with the account may remain.',
  includes:
    'That data includes profile details, conversation content, uploaded documents, RAG vectors, and authentication event records.',
  purposes: 'The data may remain for administration, security, and audit purposes.',
  retention:
    'There is currently no automatic hard-purge schedule, and soft-deleted data is retained until a future purge process is implemented.',
  request:
    'It is also retained until we process a separate retention or erasure request through the contact path below.',
  noClaim: 'We will not claim complete erasure while soft-delete-only remains in effect.',
  contact:
    'If you cannot sign in to complete in-app deletion, contact AI ML Operations, LLC via aimloperations.com.',
  email: 'Include the email address used for your Hesychia account in that request.',
  days: 'We aim to respond to account-deletion requests within 30 days.',
  staff: 'Staff accounts cannot self-delete in the app, so use the contact path above.',
};

const TERMS = [
  'These Terms of Service are a legally binding agreement between you and AI ML Operations, LLC.',
  'The agreement governs access to and use of the Hesychia chat service.',
];

const LEGAL_TERMS = [
  'This is the public copy of the Hesychia Terms of Service, last updated July 27, 2026.',
  'The terms govern use of the chat service operated by AI ML Operations, LLC.',
];

/** @type {Record<string, string[]>} */
const PAGE_SENTENCES = {
  '/': Object.values(HOME),
  '/pricing/': Object.values(PRICING),
  '/privacy/': Object.values(PRIVACY),
  '/changelog/': Object.values(CHANGELOG),
  '/terms_of_service/': TERMS,
  '/legal/terms-of-service': LEGAL_TERMS,
  '/account-deletion/': Object.values(ACCOUNT_DELETION),
  '/signin/': [
    'Sign in to an existing Hesychia account with email and password, or use the SSO buttons when registration is open.',
    'New visitors can create an account on the sign-up page.',
  ],
  '/signup/': [
    'Create a Hesychia account, then complete payment on Stripe for the public price of $10/month with no ads and no upsells.',
    'Sign in on the sign-in page if you already have a Hesychia account.',
  ],
  '/auth/callback/': [
    'Hesychia is finishing sign-in and sending you back to the app.',
    'If the provider returns an error, this page shows it and links back to the sign-in form.',
  ],
  '/billing/success': [
    'Your Hesychia subscription checkout completed and no further payment step is required here.',
    'Sign in with the account you just created to continue into the app.',
  ],
  '/billing/cancel': [
    'No charge was made, and your Hesychia account may already exist.',
    'Sign in and open billing again, or return to sign up to retry Stripe Checkout.',
  ],
  '/password_reset/': [
    'Enter the email on your Hesychia account and we will send a link to set a new password.',
    'The link opens a page where you choose that password.',
  ],
  '/password_reset_confirmation/': [
    'If an account exists for that address, Hesychia sent a link to set a new password.',
    'Check the inbox for that email, then return to sign in.',
  ],
  '/set_password/': [
    'Choose a new password for your Hesychia account using the link from the reset email.',
    'After the new password is saved, sign in with that password.',
  ],
  '/account/': [
    'View the Hesychia account profile and subscription on this page.',
    'Upgrade, change, or cancel the plan, and company managers can invite or update team accounts.',
  ],
  '/document_storage': [
    'Upload documents to Hesychia and search them from chat, and personal and company libraries stay separate.',
    'Files remain until you delete them or delete the account.',
  ],
  '/feedback/': [
    'Submit feedback about Hesychia and read feedback you already sent.',
    'The form records the note you write, and it does not change your subscription or documents.',
  ],
  '/analytics/': [
    'See Hesychia usage for your account, including prompt volume, conversation volume, team seat activity, and response times.',
    'Company managers can open the company insights for this account.',
  ],
};

const NOT_FOUND_SENTENCES = [
  'The page is not found, and this address is not a Hesychia page.',
  'Open the home page to read what the product does, what it costs, and how to start an account.',
];

/**
 * @param {string} pathname
 * @param {string[]} sentences
 */
function assertSentenceBand(pathname, sentences) {
  if (sentences.length < 2) {
    throw new Error(`${pathname} needs at least two sentences`);
  }
  const counts = sentences.map(wordCount);
  counts.forEach((count, index) => {
    if (count < 10 || count > 25) {
      throw new Error(
        `${pathname} sentence ${index + 1} is ${count} words (want 10-25): ${sentences[index]}`,
      );
    }
  });
  const average = counts.reduce((sum, count) => sum + count, 0) / counts.length;
  if (average < 10 || average > 25) {
    throw new Error(`${pathname} average sentence length is ${average}`);
  }
}

for (const pathname of PAGE_PATHS) {
  const sentences = PAGE_SENTENCES[pathname];
  if (!sentences) throw new Error(`no prose for ${pathname}`);
  assertSentenceBand(pathname, sentences);
}
assertSentenceBand('/404.html', NOT_FOUND_SENTENCES);

/**
 * @param {string} pathname
 * @returns {string[]}
 */
function sentencesForPath(pathname) {
  const sentences = PAGE_SENTENCES[pathname];
  if (!sentences) throw new Error(`no prose for ${pathname}`);
  return sentences;
}

/**
 * @param {string} pathname
 * @returns {string}
 */
function proseHtml(pathname) {
  const inner = sentencesForPath(pathname)
    .map((sentence) => `<p>${sentence}</p>`)
    .join('');
  return `<div id="crawler-prose">${inner}</div>`;
}

module.exports = {
  HOME,
  PRICING,
  PRIVACY,
  CHANGELOG,
  ACCOUNT_DELETION,
  NOT_FOUND_SENTENCES,
  sentencesForPath,
  proseHtml,
  wordCount,
};
